/**
 * Studium/School Service
 * Handles homework, exams, grades, timetable, and subject management
 */

import {
  HomeworkRecord,
  ExamsRecord,
  GradesRecord,
  CustomSubjectsRecord,
  SubjectFilesRecord,
} from '@/types/collections';
import { createRepository } from '@/services/repository';

class StudiumService {
  private homeworkRepo = createRepository<HomeworkRecord>('homework');
  private examsRepo = createRepository<ExamsRecord>('exams');
  private gradesRepo = createRepository<GradesRecord>('grades');
  private subjectsRepo = createRepository<CustomSubjectsRecord>('custom_subjects');
  private filesRepo = createRepository<SubjectFilesRecord>('subject_files');

  setUserId(userId: string): void {
    this.homeworkRepo.setUserId(userId);
    this.examsRepo.setUserId(userId);
    this.gradesRepo.setUserId(userId);
    this.subjectsRepo.setUserId(userId);
    this.filesRepo.setUserId(userId);
  }

  // Homework API
  async getAssignments(filter?: { status?: string; subject?: string }) {
    const pbFilter = [];
    if (filter?.status === 'completed') pbFilter.push('completed = true');
    if (filter?.status === 'pending') pbFilter.push('completed = false');
    if (filter?.subject) pbFilter.push(`subject = "${filter.subject}"`);

    return this.homeworkRepo.list({
      filter: pbFilter.length > 0 ? pbFilter.join(' && ') : undefined,
      sort: '-due_date',
    });
  }

  async createAssignment(data: Partial<HomeworkRecord>) {
    return this.homeworkRepo.create(data);
  }

  async updateAssignmentStatus(assignmentId: string, completed: boolean) {
    return this.homeworkRepo.update(assignmentId, { completed, completed_date: new Date().toISOString() } as Partial<HomeworkRecord>);
  }

  // Exams API
  async getExams() {
    return this.examsRepo.list({
      sort: 'date',
    });
  }

  async createExam(data: Partial<ExamsRecord>) {
    return this.examsRepo.create(data);
  }

  async updateExam(id: string, data: Partial<ExamsRecord>) {
    return this.examsRepo.update(id, data);
  }

  // Grades API
  async getGrades() {
    return this.gradesRepo.list({
      sort: '-date',
    });
  }

  async logGrade(data: Partial<GradesRecord>) {
    return this.gradesRepo.create(data);
  }

  // Schedule API
  async getSchedule() {
    // This is handled by calendarService.getTimetable()
    return { message: 'Use calendarService.getTimetable()' };
  }

  // Files API
  async listFiles(courseId?: string) {
    return this.filesRepo.list({
      filter: courseId ? `subject = "${courseId}"` : undefined,
      sort: '-uploaded_date',
    });
  }

  async uploadFile(data: Partial<SubjectFilesRecord>) {
    return this.filesRepo.create(data);
  }
}

export const studiumService = new StudiumService();

